import type { EventProperties, Options } from '@segment/analytics-next';
import type { SegmentClientName } from '@/typings/segment-manager';

export const localStorageSegmentDebugEnabledKey = 'segmentDebugEnabled';

type SegmentDebugStatus = 'queued' | 'dispatching' | 'beacon accepted' | 'beacon rejected';

interface SegmentDebugEvent {
  readonly source: SegmentClientName;
  readonly status: SegmentDebugStatus;
  readonly name: string;
}

const sensitiveKeyPattern = new RegExp(
  'email|phone|mobile|first.?name|last.?name|full.?name|address|token|authorization|password|secret|' +
  'credit.?card|card.?number|cvv|cvc|security.?code|expir(?:y|ation)|reservation.?group.?id|reservation.?id',
  'iu'
);

const reservationGroupIdPattern = /\/reservation\/[a-zA-Z0-9-]+/gu;

export function hasDebugEnabled(): boolean {
  try {
    const storedValue = localStorage.getItem(localStorageSegmentDebugEnabledKey);

    if (storedValue === null) {
      return false;
    }

    return storedValue === 'true';
  } catch {
    return false;
  }
}

export function toggleSegmentDebug(): void {
  try {
    const isEnabled = hasDebugEnabled();

    localStorage.setItem(localStorageSegmentDebugEnabledKey, isEnabled ? 'false' : 'true');
  } catch {
    // Debug controls must not affect event delivery.
  }
}

function sanitizeDebugValue(value: unknown, key = ''): unknown {
  if (sensitiveKeyPattern.test(key)) {
    return '[REDACTED]';
  }

  if (typeof value === 'string') {
    return value.replace(reservationGroupIdPattern, '/reservation/MASKED');
  }

  if (Array.isArray(value)) {
    return value.map(item => sanitizeDebugValue(item));
  }

  if (typeof value === 'object' && value !== null) {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [childKey, sanitizeDebugValue(childValue, childKey)])
    );
  }

  return value;
}

export function debugSegmentEvent(
  event: SegmentDebugEvent,
  properties?: EventProperties,
  options?: Options
): void {
  try {
    if (hasDebugEnabled() === false) {
      return;
    }

    // oxlint-disable-next-line no-console -- Explicitly enabled console diagnostics.
    console.info(
      `%cSegment Event%c [${event.source}] [${event.status}] ${event.name} %o %o`,
      'color: #5a2; font-weight: 700; border: 1px solid #5a2; padding: 2px 5px; border-radius: 5px;',
      'color: #c842b2; font-weight: 700;',
      sanitizeDebugValue(properties),
      sanitizeDebugValue(options)
    );
  } catch {
    // Logging must not affect event delivery.
  }
}

export function debugSegmentInitialization(message: string): void {
  try {
    if (hasDebugEnabled() === false) {
      return;
    }

    // oxlint-disable-next-line no-console -- Explicitly enabled console diagnostics.
    console.info(`%cSegment Init%c ${message}`,
      'color: rgb(200, 95, 66); font-weight: 700; border: 1px solid rgb(200, 95, 66);' +
      'padding: 2px 5px; border-radius: 5px;',
      'color:rgb(200, 95, 66); font-weight: 700;');
  } catch {
    // Logging must not affect event delivery.
  }
}
